/**
 * @fileoverview `EventTitleBlock` (WEB-705) — the category-tinted title block:
 * chips, category eyebrow, `<h1>`, standfirst.
 *
 * Mounts into `EventSpineTemplate`'s `titleBlock` slot (WEB-704) as the first
 * thing in the editorial column — "the highest-contrast moment on the page
 * and the one that has to read correctly for all ten [category] slugs."
 *
 * ## Composition, top to bottom
 *
 * 1. **Eyebrow row** — the category pill (dot + label, chroma from
 *    {@link getCategoryChroma}), then a doors/check-in time fact chip
 *    (WEB-1228 R10: the editorial top-pick chip was removed — no write path).
 *    The chip is sourced from a real field; an absent source omits the chip rather than rendering a
 *    fabricated placeholder ("TBA", etc.) — see {@link buildDoorsChipLabel}.
 *    The row spends its ONE accent on the category pill: the fact chip uses
 *    the neutral {@link CHIP_SHELL_CLASS} glass shell (WEB-737).
 * 2. **`<h1>`** — the event title at `var(--evt-h1)` (WEB-702 size ladder),
 *    `text-wrap: balance`, `line-height: 1.03`. This is the page's ONE `<h1>`;
 *    the legacy `EventHero` also renders one today, but that lives in the old
 *    `EventDetailTemplate` tree, which this spine does not mount alongside
 *    (WEB-704 file header).
 * 3. **Standfirst** — a `<p>` at `var(--evt-stand)`, capped `max-width: 60ch`,
 *    `text-wrap: pretty`. Composed from the date (projected onto the venue's
 *    IANA timezone via the single PIP-98 formatter {@link formatEventWhen} —
 *    never re-derived here), the venue name, and a category-shaped third
 *    clause the caller supplies (`standfirstDetail`). Segments that don't
 *    resolve (unparseable date, blank detail) are dropped rather than
 *    rendered as a dangling `·`.
 *
 * ## Pure props, no data fetching
 *
 * Every value — including `theme` — is caller-supplied. `getCategoryChroma`
 * takes an explicit theme because it derives a concrete, AA-safe hex ink per
 * theme (WEB-701/WEB-728); a molecule that read `useTheme()` itself would
 * violate the "pure props, no hooks that read context" molecule convention,
 * so the caller (the eventual page/connected wrapper) resolves the theme and
 * passes it down — the same shape `CategoryChip` in `@loci/ui` already uses
 * for its caller-resolved `activeColor` / `activeForeground`.
 *
 * `formatEventWhen` is a plain pure function (not a hook), so calling it here
 * does not violate that rule either — it is the same posture `EventHeroView`
 * takes with its pre-computed `whenLabel`, just resolved a layer closer to the
 * view since this molecule owns its own standfirst composition.
 *
 * @since WEB-705
 */
import * as React from 'react';

import { formatEventWhen } from '@loci/types';

import { cn } from '@/components/atoms/primitives/utils';
import { ResaleMarker } from '@/components/atoms/ResaleMarker';
import { getEventLexicon } from '@/config/eventLexicon';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { DisplayEventTitle } from '@/lib/format/DisplayEventTitle';
import { EVT_MICRO_LABEL_10_MONO_CLASS, EVT_MICRO_LABEL_PILL_CLASS } from '@/lib/eventV2MicroLabel';
import type { EventDecisionPanel } from '@/types/eventDetail';
import { getCategoryChroma } from '@/utils/categoryColors';

/** Theme driving {@link getCategoryChroma}'s ink/fill/border. Caller-resolved. */
export type EventTitleBlockTheme = 'light' | 'dark';

/**
 * Doors/check-in fact-chip source — the same two fields the decision panel
 * (WEB-706 / API-1661) surfaces. `headline` is `null` for two honest reasons
 * (no separate doors time published, or `startTimeKnown: false`) — either way
 * the chip omits itself rather than rendering a fabricated time.
 */
export type EventTitleBlockDoorsChip = Pick<EventDecisionPanel, 'kicker' | 'headline'>;

export interface EventTitleBlockProps {
  /**
   * Category slug driving the lexicon label and the chroma resolver. Anything
   * outside the canonical ten — including `null`/blank — resolves to the
   * generic lexicon word and the CLT-skyline fallback chroma, a normal
   * outcome (WEB-703 / WEB-701), never an error state.
   */
  categorySlug?: string | null;
  /** See {@link EventTitleBlockTheme}. */
  theme: EventTitleBlockTheme;
  /** Event title — rendered as the page's one `<h1>`. */
  title: string;
  /** Event start instant (ISO 8601 or `Date`), projected via `formatEventWhen`. */
  startsAt: string | Date;
  /**
   * Server-authoritative IANA venue timezone (PIP-98). `null`/omitted falls
   * back to UTC — `formatEventWhen`'s own convention — never the viewer's
   * locale.
   */
  timezone?: string | null;
  /**
   * Mirrors `Event.startTimeKnown`. `false` suppresses the standfirst's time
   * component (there isn't one here — only `dateLine` is used — so this only
   * affects nothing at present, but is threaded through so callers pass the
   * same honest signal `formatEventWhen` expects everywhere else). Defaults
   * `true`, matching the Prisma column default and `formatEventWhen`'s own.
   */
  startTimeKnown?: boolean;
  /** Venue display name — the standfirst's second clause. */
  venueName: string;
  /**
   * Resolved `/venue/{slug}` href for the standfirst venue clause (WEB-809).
   * Absent/blank → the venue name is plain text, never a dead link.
   */
  venueHref?: string | null;
  /**
   * Category-shaped third clause for the standfirst, e.g. `"18+"`,
   * `"BYOB"`, `"Bring a mat"`. Omitted entirely when `null`/blank — never a
   * fabricated filler clause.
   */
  standfirstDetail?: string | null;
  /**
   * Doors/check-in chip source. `null`/omitted, or a `null` `headline`, omits
   * the chip — never a "TBA" placeholder.
   */
  decisionPanel?: EventTitleBlockDoorsChip | null;
  /**
   * WEB-1098 — `true` for a confirmed Ticketmaster resale listing: the DS
   * 1.10.0 `.a-resale` marker renders as the LAST item of the fact row. An
   * unknown or primary verdict renders nothing.
   */
  resaleListing?: boolean;
  /** Optional additional class names on the outer wrapper. */
  className?: string;
}

/**
 * The ONE shell every fact chip in the eyebrow row wears (WEB-737). Fill and
 * border come from the themed `--evt-chip-*` pair in `styles/tokens/color.css`
 * / `dark.css`; there is no `backdrop-filter` — the design has none on chips,
 * and the removed `backdrop-blur-[10px]` bought nothing over an opaque
 * editorial column while costing a compositing layer per chip.
 *
 * Nothing below this constant may add a fill or a border. The eyebrow row has
 * exactly one accent to spend and the category pill spends it; a second tinted
 * shell makes two elements compete for the same "this one matters" reading.
 * `EventTitleBlock.test.tsx` asserts the two chips' class sets differ by their
 * `text-*` token alone, so a tint cannot creep back in silently.
 */
const CHIP_SHELL_CLASS = cn(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-evt-chip-border bg-evt-chip-bg px-3 py-[6px]',
  EVT_MICRO_LABEL_10_MONO_CLASS,
);

/** Neutral ink — `--fg-2`, the quiet-but-legible rung, not full `--foreground`. */
const DOORS_CHIP_CLASS = cn(CHIP_SHELL_CLASS, 'text-fg-2');

/**
 * Compose the doors/check-in chip label (e.g. `"DOORS 7:00 PM"`), or `null`
 * when the source is absent — no decision panel, or a `null`/blank headline
 * (WEB-706 / API-1676: `startTimeKnown: false`, or neither doors nor start
 * could be formatted). Never fabricates a "TBA" chip.
 */
function buildDoorsChipLabel(
  decisionPanel: EventTitleBlockDoorsChip | null | undefined,
): string | null {
  if (!decisionPanel) return null;
  const headline = decisionPanel.headline;
  if (typeof headline !== 'string' || headline.trim().length === 0) return null;
  const kicker = decisionPanel.kicker.trim();
  return kicker.length > 0 ? `${kicker} ${headline}` : headline;
}

/**
 * Compose the standfirst from date · venue · category-shaped detail, dropping
 * any segment that doesn't resolve (unparseable date → empty `dateLine`;
 * blank/omitted `detail`) rather than emitting a dangling separator. Mirrors
 * `EventHero`'s `buildStructuredSubtitle` convention (WEB-454).
 *
 * WEB-809: when `venueHref` is a real destination the venue clause is an
 * `<a>`; otherwise it stays plain text. Callers that only have a fallback
 * name ("Venue TBA") must omit the href.
 */
function buildStandfirstSegments(
  dateLine: string,
  venueName: string,
  venueHref: string | null,
  detail: string | null | undefined,
): React.ReactNode[] {
  const trimmedDetail =
    typeof detail === 'string' && detail.trim().length > 0 ? detail.trim() : null;
  const trimmedVenue = venueName.trim();
  const href =
    typeof venueHref === 'string' && venueHref.trim().length > 0 ? venueHref.trim() : null;

  const venueSegment: React.ReactNode =
    trimmedVenue.length === 0 ? null : href !== null ? (
      <a
        href={href}
        className="text-inherit no-underline hover:underline! focus-visible:underline! focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg-1"
        data-testid="event-title-block-venue-link"
      >
        {trimmedVenue}
      </a>
    ) : (
      trimmedVenue
    );

  return [dateLine, venueSegment, trimmedDetail].filter(
    (segment) => segment !== null && segment !== undefined && segment !== '',
  );
}

/**
 * EventTitleBlock — pure-props View. See the file header for the structural
 * contract and honesty rules.
 */
export function EventTitleBlock({
  categorySlug,
  theme,
  title,
  startsAt,
  timezone,
  startTimeKnown = true,
  venueName,
  venueHref,
  standfirstDetail,
  decisionPanel,
  resaleListing = false,
  className,
}: EventTitleBlockProps): React.ReactElement {
  // Trim once and reuse for both the lexicon label and the chroma lookup, so
  // the two can never resolve two different slugs from the same input
  // (mirrors EventSpineTemplate's own slug handling, WEB-704).
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const lexicon = getEventLexicon(slug);
  const chroma = getCategoryChroma(slug, theme);
  const categoryLabel = categoryDisplayLabel(lexicon.breadcrumbCategoryLabel);

  const doorsChipLabel = buildDoorsChipLabel(decisionPanel);

  const when = formatEventWhen(startsAt, timezone ?? undefined, startTimeKnown);
  const standfirstSegments = buildStandfirstSegments(
    when.dateLine,
    venueName,
    venueHref ?? null,
    standfirstDetail,
  );

  return (
    // `event-spine-title-block`, not `event-title-block` (M5, WEB-705): the
    // legacy `components/organisms/EventDetail/EventTitleBlock.tsx` already
    // emits `data-testid="event-title-block"` on the live `/event/[slug]`
    // route. Reusing that literal here would strict-mode-violate every
    // locator that resolves it once both components can mount on the same
    // page post-M5 cutover — see `EventDetailPage.ts`'s `titleBlock()`, which
    // intentionally still targets the legacy testid and is NOT this component.
    <div className={cn('event-title-block', className)} data-testid="event-spine-title-block">
      <div
        className="mb-(--evt-mgap,20px) flex flex-wrap items-center gap-2"
        data-testid="event-title-block-eyebrow"
      >
        <span
          className={cn(
            'inline-flex h-[28px] items-center gap-1.5 rounded-full border px-[11px] py-[5px]',
            EVT_MICRO_LABEL_PILL_CLASS,
          )}
          style={{
            backgroundColor: chroma.catSoft,
            borderColor: chroma.catLine,
            color: chroma.catInk,
          }}
          data-testid="event-title-block-pill"
        >
          <span
            aria-hidden="true"
            className="size-[6px] shrink-0 rounded-full"
            style={{ backgroundColor: chroma.cat }}
          />
          {categoryLabel}
        </span>

        {doorsChipLabel !== null && (
          <span className={DOORS_CHIP_CLASS} data-testid="event-title-block-doors-chip">
            {doorsChipLabel}
          </span>
        )}

        {/* WEB-1098 — last item of the fact row (DS 1.10.0, DES-98 §1). Its own
            neutral tag, never a chip shell and never a category ink. */}
        {resaleListing === true && <ResaleMarker data-testid="event-title-block-resale-marker" />}
      </div>

      <h1
        // `wrap-break-word` (`overflow-wrap: break-word`) is load-bearing at
        // the mobile rung (WEB-705 H2): `text-wrap: balance` and normal word
        // wrapping only break at whitespace, so a single long unbroken token
        // (a title with no spaces) would otherwise spill past the 375px
        // column instead of breaking mid-word. Confirmed by
        // `event-title-block.spec.ts`'s 60-char unbroken-token fixture.
        className="m-0 text-balance wrap-break-word font-bold leading-[1.03] tracking-tight text-foreground font-display text-(length:--evt-h1)"
        data-testid="event-title-block-h1"
      >
        {/* WEB-1135: shouting feed titles are title-cased here only. The
            accessible name, and every other use of `title`, stays as published. */}
        <DisplayEventTitle title={title} />
      </h1>

      {standfirstSegments.length > 0 && (
        <p
          className="m-0 mt-(--evt-mgap,20px) max-w-[60ch] text-(length:--evt-stand) text-muted-foreground [text-wrap:pretty]"
          data-testid="event-title-block-standfirst"
        >
          {standfirstSegments.map((segment, index) => (
            <React.Fragment key={index}>
              {index > 0 ? ' · ' : null}
              {segment}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
}
